import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquarePlus, Send, Trash2, Clock } from 'lucide-react';
import {
  Avatar,
  Button,
  EmptyState,
  IconButton,
  ScrollArea,
  Spinner,
  Textarea,
  Tooltip,
} from '@ntconsult/aluma-react';
import { useAuth } from '@features/auth';
import { formatRelativeTime, formatDuration } from '@common/utils/format';
import {
  useCreateComment,
  useDeleteComment,
  useFileComments,
} from '../../hooks/useFileComments';
import { renderCommentWithTimestamps } from '../../utils/timestamps';

interface VideoCommentsTabProps {
  fileId: string;
  currentTime?: number;
  onSeek?: (seconds: number) => void;
}

export function VideoCommentsTab({ fileId, currentTime = 0, onSeek }: VideoCommentsTabProps) {
  const { t } = useTranslation('video');
  const { user } = useAuth();

  const [commentText, setCommentText] = useState('');
  const { data: comments = [], isLoading } = useFileComments(fileId);
  const { mutate: createComment, isPending: sending } = useCreateComment(fileId);
  const { mutate: deleteComment } = useDeleteComment(fileId);

  const handleSend = () => {
    const content = commentText.trim();
    if (!content) return;
    createComment(content, { onSuccess: () => setCommentText('') });
  };

  const handleAttachTime = () => {
    const stamp = formatDuration(Math.floor(currentTime));
    setCommentText((prev) => (prev ? `${prev} ${stamp} ` : `${stamp} `));
  };

  const currentStamp = formatDuration(Math.floor(currentTime));

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Textarea
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          placeholder={t('comments.placeholder')}
          rows={3}
          resize="none"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSend();
          }}
        />
        <div className="flex items-center justify-between">
          {onSeek && currentTime > 0 ? (
            <Tooltip content={t('comments.attachTime', { time: currentStamp })}>
              <IconButton
                variant="ghost"
                size="sm"
                label={t('comments.attachTime', { time: currentStamp })}
                icon={<Clock className="w-4 h-4" aria-hidden="true" />}
                onClick={handleAttachTime}
              />
            </Tooltip>
          ) : (
            <span />
          )}
          <Button
            size="sm"
            onClick={handleSend}
            disabled={!commentText.trim() || sending}
            loading={sending}
            leftIcon={<Send className="w-3.5 h-3.5" aria-hidden="true" />}
          >
            {t('comments.submit')}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-4">
          <Spinner size="sm" />
        </div>
      ) : comments.length === 0 ? (
        <EmptyState
          decorator={
            <MessageSquarePlus className="w-6 h-6 text-foreground-subtle" aria-hidden="true" />
          }
          decoratorPlacement="start"
          align="start"
          title={t('comments.noComments')}
          description={t('comments.noCommentsHint')}
          size="sm"
        />
      ) : (
        <ScrollArea className="max-h-[60vh] pr-2">
          <ul className="space-y-3">
            {comments.map((comment) => (
              <li key={comment.id} className="flex gap-3">
                <Avatar
                  name={comment.user?.name || '?'}
                  size="sm"
                  tone="brand"
                  className="flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-baseline gap-1.5 min-w-0">
                      <span className="text-xs font-semibold text-foreground truncate">
                        {comment.user?.name || '—'}
                      </span>
                      <span className="text-xs text-foreground-subtle">
                        {formatRelativeTime(comment.createdAt)}
                      </span>
                    </div>
                    {(comment.userId === user?.id || user?.role === 'admin') && (
                      <IconButton
                        variant="ghost"
                        size="xs"
                        onClick={() => deleteComment(comment.id)}
                        label={t('comments.delete')}
                        icon={<Trash2 className="w-3.5 h-3.5" aria-hidden="true" />}
                      />
                    )}
                  </div>
                  <p className="text-sm text-foreground mt-0.5 whitespace-pre-wrap break-words">
                    {onSeek
                      ? renderCommentWithTimestamps(comment.content, onSeek, (s) =>
                          t('comments.goToTime', { time: s }),
                        )
                      : comment.content}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </ScrollArea>
      )}
    </div>
  );
}
